const S = {
  container: '.must-haves__container',
  item: '.must-haves__item',
  options: '.must-haves__item--options',
  switcher: '.must-haves__toggle-input',
  variantsContainer: '.must-haves__item--variants',
  selects: '.select__select',
  selectColors: '.select__select--color',
  bundleAddToCart: '.must-haves__footer--atc-button',
  hidden: 'hidden',
  active: 'is-active',
  prices: '.must-haves__item--prices',
  originalPrice: '.must-haves__item--price',
  price: '.must-haves__item--original-price',
  compareAtPrice: '.must-haves__item--compare-at-price',
  subtotal: '.must-haves__footer--subtotal-price',
  subtotalComparePrice: '.must-haves__footer--subtotal-compare-price',
};

class MustHaves {
    constructor() {
        this.container = document.querySelector(S.container);
        if (!this.container) return;

        this.switchers = Array.from(this.container.querySelectorAll(S.switcher));
        this.selects = Array.from(this.container.querySelectorAll(`${S.variantsContainer} ${ S.selects }`));

        this.state = {};

        this.subtotalFinalPrice = 0;
        this.subtotalComparePrice = 0;

        this.switchers.forEach(switcher => {
          this.state[switcher.dataset.productId] = {
            id: Number(switcher.dataset.variantId),
            price: Number(switcher.dataset.variantPrice),
            compareAtPrice: Number(switcher.dataset.variantComparePrice),
            quantity: 1,
            discount: Number(switcher.dataset.discount) || null,
            switchEnabled: switcher.checked,
            available: switcher.dataset.variantAvailable === 'true'
          }
        })
    
        this.container.addEventListener('click', this._onClick.bind(this));
        this.switchers.forEach(switcher => switcher.addEventListener('change', this._onSwitcherChange.bind(this)))
        this.selects.forEach(select => select.addEventListener('change', this._onVariantChange.bind(this)));

        this._registerSubtotalUpdateEvent();
        this._setColorAvailability();
    }

    _onClick(e) {
      if (e.target.matches(S.bundleAddToCart)) this._bundleAddToCart(e.target);
    }

    _onVariantChange(e) {
      const target = e.target;
      const item = target?.closest(S.item);
      if (!item) return;

      const productData = JSON.parse(item.querySelector('script[type="application/json"]')?.textContent);
      const group = Array.from(item.querySelectorAll(S.selects));
      if (!productData || !group.length) return;

      const selectedData = group.map(select => {
        return {
          option: select.dataset.option,
          value: select.value
        };
      })

      const selectedVariant = productData.variants.find(variant => {
        return selectedData.every(data => {
          return variant.options.includes(data.value);
        });
      });

      this._updatePriceDisplay(item, selectedVariant.price, selectedVariant.compare_at_price, productData.id);

      this.state[productData.id].id = selectedVariant.id;

      if(selectedVariant.featured_image?.src) this._updateImage(item, selectedVariant.featured_image.src, selectedVariant.featured_image.alt || productData.title);

      const colorChanged = target.getAttribute('data-option') === 'Color';
      if (colorChanged) this._updateSwatch(item, target);

      const sizeChanged = target.getAttribute('data-option') === 'Size';
      if (sizeChanged) this._checkColorAvailability(item, target, productData);

      if(!selectedVariant.available) return

      this.state[productData.id].id = selectedVariant.id;
      this.state[productData.id].available = selectedVariant.available;
      this.state[productData.id].price = selectedVariant.price;
      this.state[productData.id].compareAtPrice = selectedVariant.compare_at_price;

      this._updateSubtotalDisplay();
    }

    _onSwitcherChange(switcher) {
      const variantContainer = switcher.target.closest(S.item).querySelector(S.options);
      if (variantContainer) {
        const isVisible = switcher.target.checked;
        variantContainer.classList.toggle(S.hidden, !isVisible);
        variantContainer.setAttribute('aria-hidden', String(!isVisible));
      }

      const productId = switcher.target.dataset.productId;
      this.state[productId].switchEnabled = switcher.target.checked;

      this._updateSubtotalDisplay();
    }
    
    _toggleActive(group, activeEl) {
      group.forEach(el => el.classList.remove(S.active));
      activeEl.classList.add(S.active);
    }

    _showAlert(
        message,
        {
          container = document.body,
          baseClass = 'must-haves-alert-box',
          textClassSuffix = '--text',
          activeClass = 'is-active',
          showDelay = 50,
          visibleDuration = 3000
        } = {}
      ) {
        const existing = container.querySelector(`.${baseClass}`);
        if (existing) existing.remove();
      
        const alertBox = document.createElement('div');
        alertBox.className = baseClass;
        alertBox.setAttribute('role', 'alert');
        alertBox.setAttribute('aria-live', 'assertive');
        alertBox.setAttribute('aria-atomic', 'true');

        const alertText = document.createElement('span');
        alertText.className = baseClass + textClassSuffix;
        alertText.textContent = message;
      
        alertBox.appendChild(alertText);
        container.appendChild(alertBox);
      
        requestAnimationFrame(() => {
          setTimeout(() => alertBox.classList.add(activeClass), showDelay);
        });
      
        return new Promise((resolve) => {
          setTimeout(() => {
            alertBox.classList.remove(activeClass);
      
            const onTransitionEnd = (e) => {
              if (e.target === alertBox) {
                alertBox.removeEventListener('transitionend', onTransitionEnd);
                alertBox.remove();
                resolve();
              }
            };
            alertBox.addEventListener('transitionend', onTransitionEnd);
          }, visibleDuration);
        });
    }

    _updatePriceDisplay(item, price, compare_at_price, productId) {
      if (!item || !price || !productId) return;
      const priceContainer = item.querySelector(S.prices);
      const priceElement = priceContainer.querySelector(S.price);
      const originalPriceElement = priceContainer.querySelector(S.originalPrice);
      const compareAtPriceElement = priceContainer.querySelector(S.compareAtPrice);
      if (priceElement) {
        priceElement.textContent = this._formatPrice(price);
        return;
      }
      originalPriceElement.textContent = this._formatPrice(compare_at_price);
      compareAtPriceElement.textContent = this._formatPrice(price);
    }

    _updateImage(item, url, altText) {
      if (!item || !url) return;
      const img = item.querySelector('.must-haves__item--image img');
      img.setAttribute('src', url);
      if (altText) img.setAttribute('alt', altText);
    }

    _setColorAvailability() {
      const items = this.container.querySelectorAll(S.item);
      items.forEach(item => {
        const selectColors = item.querySelector(S.selectColors);
        const selectSize = item.querySelector(`${S.selects}[data-option="Size"]`);
        const productData = JSON.parse(item.querySelector('script[type="application/json"]').textContent);

        if (!selectColors || !productData) return;

        selectColors && selectSize 
          ? this._checkColorAvailability(item, selectSize, productData)
          : this._checkOnlyColorAvailability(item, selectColors, productData);
      });
    }

    _checkOnlyColorAvailability(item, selectColors, productData) {
      if (!item || !selectColors || !productData) return;

      const unavailableColors = productData.variants
        .filter(variant => !variant.available)
        .map(variant => variant.option1);

      this._disableUnavailableColors(selectColors, unavailableColors);

      this._autoSelectAvailableColor(selectColors, unavailableColors);
      this._updateSwatch(item, selectColors);
    }

    _checkColorAvailability(item, target, productData) {
      if (!item || !target || !productData) return;

      const selectedSizeIndex = target.selectedIndex;
      const selectColors = item.querySelector(S.selectColors);
      const selectedSize = target.options[selectedSizeIndex].text;

      const unavailableColors = this._getUnavailableColors(selectedSize, productData);

      this._disableUnavailableColors(selectColors, unavailableColors);

      this._autoSelectAvailableColor(selectColors, unavailableColors);
    }

    _getUnavailableColors(selectedSize, productData) {
      return productData.variants
        .filter(variant => variant.option1 === selectedSize && !variant.available)
        .map(variant => variant.option2);
    }

    _disableUnavailableColors(selectColors, unavailableColors) {
      Array.from(selectColors.options).forEach(option => {
        option.disabled = unavailableColors.includes(option.value);
      });
    }

    _autoSelectAvailableColor(selectColors, unavailableColors) {
      const currentColor = selectColors.value;

      if (unavailableColors.includes(currentColor)) {
        const availableOption = Array.from(selectColors.options).find(option => !option.disabled);
        if (availableOption) {
          selectColors.selectedIndex = Array.from(selectColors.options).indexOf(availableOption);
          selectColors.dispatchEvent(new Event('change'));
        }
      }
    }

    _updateSwatch(item, target) {
      if(!item || !target) return;
      const swatch = item.querySelector('.color-swatch');
      const colorValue = target.options[target.selectedIndex].value
      const handle = colorValue.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '');
      const url = `https://cdn.shopify.com/s/files/1/0013/6698/2692/files/${handle}.png`
      swatch.style.backgroundImage = `url(${url})`;
    }

    _updateSubtotalDisplay() {
      const selectedMainVariantPrice = this.container.querySelector(S.bundleAddToCart).dataset.mainVariantPrice;
      const selectedMainComparePrice = this.container.querySelector(S.bundleAddToCart).dataset.mainComparePrice;
      const mainVariantAvailable = this.container.querySelector(S.bundleAddToCart).dataset.mainVariantAvailable === 'true';

      const subtotalElement = this.container.querySelector(S.subtotal);
      const subtotalComparePriceElement = this.container.querySelector(S.subtotalComparePrice);

      this.subtotalFinalPrice = Object.values(this.state)
        .filter(item => item.switchEnabled && item.available)
        .reduce((acc, item) => acc + item.price, 0);

      this.subtotalComparePrice = Object.values(this.state)
        .filter(item => item.switchEnabled && item.available)
        .reduce((acc, item) => acc + (Number(item.compareAtPrice) ? item.compareAtPrice : item.price), 0);

      if (mainVariantAvailable) this.subtotalFinalPrice += Number(selectedMainVariantPrice);
      if (mainVariantAvailable) this.subtotalComparePrice += Number(selectedMainComparePrice) ? Number(selectedMainComparePrice) : Number(selectedMainVariantPrice);

      if (subtotalElement && subtotalComparePriceElement) {
        subtotalElement.textContent = Shopify.formatMoney(this.subtotalFinalPrice, Shopify.money_format);
        subtotalComparePriceElement.textContent = Shopify.formatMoney(this.subtotalComparePrice, Shopify.money_format);

        if (this.subtotalFinalPrice === this.subtotalComparePrice) {
          subtotalComparePriceElement.classList.add(S.hidden);
          subtotalComparePriceElement.setAttribute('aria-hidden', 'true');
          return;
        }

        subtotalComparePriceElement.classList.remove(S.hidden);
        subtotalComparePriceElement.setAttribute('aria-hidden', 'false');
      }
    }

    _formatPrice(price) {
      if (typeof price === 'number') {
        const decimal = price % 1;
        if (decimal === 0.5) {
          price = Math.ceil(price);
        }
      }

      return Shopify.formatMoney(price, Shopify.money_format); 
    }

    _registerSubtotalUpdateEvent() {
      window.addEventListener('must-haves:main-variant-change', (e) => {
        const { 
          finalPrice, 
          compareAtPrice,
          variantId,
          variantAvailable
        } = e.detail;

        const addToCartBtn = this.container.querySelector(S.bundleAddToCart);
        if (!addToCartBtn) return;

        addToCartBtn.setAttribute('data-main-variant-price', variantAvailable ? finalPrice : 0);
        addToCartBtn.setAttribute('data-main-compare-price', variantAvailable ? (compareAtPrice || 0) : 0);
        addToCartBtn.setAttribute('data-main-variant-id', variantId);
        addToCartBtn.setAttribute('data-main-variant-available', variantAvailable);

        this._updateSubtotalDisplay();
      })
    }

    async _bundleAddToCart() {
        let itemsToAnalyze = Object.values(this.state).filter(item => item.switchEnabled);
        const mainVariantId = this.container.querySelector(S.bundleAddToCart).dataset.mainVariantId;
        const mainVariantAvailable = this.container.querySelector(S.bundleAddToCart).dataset.mainVariantAvailable === 'true';
        if (mainVariantAvailable) {
          itemsToAnalyze.push({
            id: mainVariantId,
            quantity: 1,
          });
        }

        const body = {
          items: itemsToAnalyze.map(item => {
            return {
              id: item.id,
              quantity: item.quantity,
              properties: { '_must-haves-discount': item.discount }
            }
          })
        }

        const req = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        })

        await req.json();
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new MustHaves();
})